import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AppState, AppStateThunkAction } from '@src/providers/AppStateProvider/types';
import { castDraft } from 'immer';
import type { OMSOpenViewPayload, OMSView, OMSViewLaunchDataMap } from './OMSView';
import type { Entity, OMSPopoutFeatures, OMSPopoutGroup, OMSPopoutGroupType, OMSState } from './types';

const initialState: OMSState = {
  openedView: undefined,
  popoutGroups: {},
  placedEntity: undefined,
};

// Type guard to check if payload is an object with view property
const hasOpenViewPayload = (payload: OMSView | OMSOpenViewPayload): payload is OMSOpenViewPayload => {
  return payload && typeof payload === 'object' && 'view' in payload;
};

const OMSSlice = createSlice({
  name: 'oms',
  initialState,
  reducers: {
    openView: (state, action: PayloadAction<OMSView | OMSOpenViewPayload>) => {
      const view = hasOpenViewPayload(action.payload) ? action.payload.view : action.payload;
      const openedViewLaunchData = hasOpenViewPayload(action.payload) ? action.payload.launchData : undefined;

      state.openedViewLaunchData = openedViewLaunchData;
      state.placedEntity = undefined;
      const popouts = Object.values(state.popoutGroups);
      const openPopoutGroup = popouts.find(group => group.views.includes(view));
      if (openPopoutGroup) {
        openPopoutGroup.activeView = view;
        openPopoutGroup._window?.focus();
        state.openedView = undefined;
      } else {
        state.openedView = view;
      }
    },
    closeView: state => {
      state.openedView = undefined;
      state.placedEntity = undefined;
    },
    openPopoutGroup: (
      state,
      action: PayloadAction<{
        name: OMSPopoutGroupType;
        activeView: OMSView;
        views: OMSView[];
        features: OMSPopoutFeatures;
      }>
    ) => {
      state.openedView = undefined;
      state.popoutGroups[action.payload.name] = action.payload;
    },
    closePopoutGroup: (state, action: PayloadAction<OMSPopoutGroupType>) => {
      state.openedView = state.openedView ?? state.popoutGroups[action.payload]?.activeView;
      delete state.popoutGroups[action.payload];
    },
    setPopoutWindow: (state, action: PayloadAction<{ name: OMSPopoutGroupType; popoutWindow: Window }>) => {
      const group = state.popoutGroups[action.payload.name];
      if (!group) {
        return;
      }
      // Need to keep a window reference to be able to focus the window
      group._window = castDraft(action.payload.popoutWindow);
    },
    updatePopoutView: (state, action: PayloadAction<{ name: OMSPopoutGroupType; view: OMSView }>) => {
      const group = state.popoutGroups[action.payload.name];
      if (!group) {
        return;
      }
      group.activeView = action.payload.view;
      state.placedEntity = undefined;
    },
    updatePopoutFeatures: (
      state,
      action: PayloadAction<{ name: OMSPopoutGroupType; features: Partial<OMSPopoutFeatures> }>
    ) => {
      const group = state.popoutGroups[action.payload.name];
      if (!group) {
        return;
      }
      group.features = { ...group.features, ...action.payload.features };
    },
    focusPopoutGroup: (state, action: PayloadAction<OMSPopoutGroupType>) => {
      const group = state.popoutGroups[action.payload];
      if (!group?._window) {
        return;
      }
      group._window.focus();
    },
    showFeedbackCard: (state, action: PayloadAction<Entity | null>) => {
      state.placedEntity = state.placedEntity ?? action.payload;
    },
    hideFeedbackCard: state => {
      state.placedEntity = undefined;
    },
    /** Load initial state from app config */
    loadOmsPopoutGroups: (
      state,
      action: PayloadAction<Partial<Record<OMSPopoutGroupType, Omit<OMSPopoutGroup, '_window'>>>>
    ) => {
      state.popoutGroups = action.payload;
    },
  },
});

export const toggleView =
  (view: OMSView): AppStateThunkAction =>
  (dispatch, getState) => {
    const currentView = getState().OMS.openedView;

    if (currentView === view) {
      dispatch(OMSSliceActions.closeView());
    } else {
      dispatch(OMSSliceActions.openView(view));
    }
  };

export const selectView = (state: AppState) => state.OMS.openedView;

// Type-safe selector for view data
export const selectViewData = <T extends keyof OMSViewLaunchDataMap>(
  state: AppState,
  view: T
): OMSViewLaunchDataMap[T] | undefined => {
  if (state.OMS.openedView === view) {
    return state.OMS.openedViewLaunchData as OMSViewLaunchDataMap[T];
  }
  return undefined;
};

export const selectOpenPopoutGroups = createSelector(
  (state: AppState) => state.OMS.popoutGroups,
  popoutGroups => {
    return Object.keys(popoutGroups).filter(
      group => popoutGroups[group as OMSPopoutGroupType]?.activeView !== undefined
    ) as OMSPopoutGroupType[];
  }
);

export const {
  openView,
  closeView,
  openPopoutGroup,
  closePopoutGroup,
  setPopoutWindow,
  updatePopoutView,
  updatePopoutFeatures,
  focusPopoutGroup,
  showFeedbackCard,
  hideFeedbackCard,
  loadOmsPopoutGroups,
} = OMSSlice.actions;

export const OMSSliceActions = { ...OMSSlice.actions };
export const OMSSliceReducer = OMSSlice.reducer;
